/**
 * Tags: the reads, and every write (`070-search-and-links.md` §6).
 *
 * **Not one of these calls `fetch`.** Every `mutationFn` hands an entry to the outbox and
 * resolves as soon as it is in IndexedDB (`112-offline-drafts.md` §5).
 *
 * The one design decision worth stating: **the client generates the tag's ULID.** It has
 * to. Assignment is `PATCH /api/pages/{id} { tagIds }` with the FULL set, so at the instant
 * the user presses Enter on "Create tag 'npc'" we must already know the id we are about to
 * put in that set. The alternative — awaiting a `POST /api/tags` response for the id — is a
 * raw write outside the outbox, and there is no version of that which is acceptable. The
 * outbox's FIFO order guarantees the `tag.create` lands before the `page.update` that
 * references it, exactly as it does for `page.create` → `page.update`.
 */
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { keys } from '@/api/keys';
import type { PageDto, PageTag } from '@/api/endpoints/pages';
import {
  getTagPages,
  getTags,
  type Tag,
  type TagPagesResponse,
  type TagsResponse,
} from '@/api/endpoints/tags';
import { toast } from '@/components/ui/sonner';
import { translate } from '@/lib/i18n';
import { ulid } from '@/lib/ulid';
import { outbox } from '@/offline/outbox';

/* ── Reads ──────────────────────────────────────────────────────────────────── */

export function useTags(): UseQueryResult<TagsResponse> {
  return useQuery({
    queryKey: keys.tags.all(),
    queryFn: getTags,
    // We are the only writer, and every tag mutation invalidates this (`111` §3.2).
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
  });
}

/** The tag filter view (`070` §6). Multi-select is explicitly not in v1 — one tag at a time. */
export function useTagPages(id: string | undefined): UseQueryResult<TagPagesResponse> {
  return useQuery({
    queryKey: keys.tags.pages(id ?? ''),
    queryFn: () => getTagPages(id as string),
    enabled: id !== undefined,
    staleTime: 60_000,
  });
}

/* ── Create ─────────────────────────────────────────────────────────────────── */

export interface CreateTagInput {
  /** Client-generated. Use `newTagId()` and keep it — the page's `tagIds` needs it NOW. */
  id: string;
  name: string;
  color?: string | null;
}

export function newTagId(): string {
  return ulid();
}

export function useCreateTag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateTagInput) =>
      outbox.enqueue({
        kind: 'tag.create',
        entityId: input.id,
        label: input.name,
        request: {
          method: 'POST',
          path: '/api/tags',
          body: { id: input.id, name: input.name.trim(), color: input.color ?? null },
        },
      }),

    // Optimistic, so the chip appears on the page the moment Enter is pressed and the
    // sidebar count moves with it.
    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey: keys.tags.all() });
      const previous = queryClient.getQueryData<TagsResponse>(keys.tags.all());

      const row: Tag = {
        id: input.id,
        name: input.name.trim(),
        color: input.color ?? null,
        pageCount: 0,
      };

      queryClient.setQueryData<TagsResponse>(
        keys.tags.all(),
        (old) => old && { ...old, tags: [...old.tags, row] },
      );

      return { previous };
    },

    onError: (_error, _input, context) => {
      if (context?.previous) queryClient.setQueryData(keys.tags.all(), context.previous);
    },
  });
}

/* ── Rename / recolour ──────────────────────────────────────────────────────── */

export interface UpdateTagInput {
  id: string;
  name?: string;
  color?: string | null;
}

/**
 * A rename requires NO page rewrites — assignment is by id, so the tag's name is looked up,
 * never copied (`070` §6, "Rename").
 */
export function useUpdateTag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...patch }: UpdateTagInput) =>
      outbox.enqueue({
        kind: 'tag.update',
        entityId: id,
        label: patch.name ?? translate('tags.fallbackLabel'),
        request: { method: 'PATCH', path: `/api/tags/${id}`, body: patch },
      }),

    onMutate: async ({ id, ...patch }) => {
      await queryClient.cancelQueries({ queryKey: keys.tags.all() });
      const previous = queryClient.getQueryData<TagsResponse>(keys.tags.all());

      queryClient.setQueryData<TagsResponse>(
        keys.tags.all(),
        (old) =>
          old && {
            ...old,
            tags: old.tags.map((tag) => (tag.id === id ? { ...tag, ...patch } : tag)),
          },
      );

      return { previous };
    },

    onError: (_error, _input, context) => {
      if (context?.previous) queryClient.setQueryData(keys.tags.all(), context.previous);
      toast.error(translate('tags.toast.renameError'));
    },
  });
}

/* ── Delete ─────────────────────────────────────────────────────────────────── */

/**
 * Cascades on `page_tag` ONLY. **It never deletes a page** — and the confirm dialog says so
 * in as many words (`070` §6; `TagsSection.tsx` owns that wording).
 */
export function useDeleteTag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      outbox.enqueue({
        kind: 'tag.delete',
        entityId: id,
        request: { method: 'DELETE', path: `/api/tags/${id}` },
      }),

    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: keys.tags.all() });
      const previous = queryClient.getQueryData<TagsResponse>(keys.tags.all());

      queryClient.setQueryData<TagsResponse>(
        keys.tags.all(),
        (old) => old && { ...old, tags: old.tags.filter((tag) => tag.id !== id) },
      );

      return { previous };
    },

    onError: (_error, _id, context) => {
      if (context?.previous) queryClient.setQueryData(keys.tags.all(), context.previous);
      toast.error(translate('tags.toast.deleteError'));
    },
  });
}

/* ── Assignment ─────────────────────────────────────────────────────────────── */

export interface SetPageTagsInput {
  pageId: string;
  /** The FULL new set. There is no add/remove-one endpoint (`040`, `070` §6). */
  tagIds: string[];
}

/**
 * `kind: 'page.update'` — the SAME kind the editor's autosave and the tree's rename use, so
 * a tag change and a content save for the same page coalesce into one `PATCH` carrying both
 * (`112` §5.2). Do not invent a `page.tags` kind: it would defeat that merge and race the
 * content save it queued behind.
 */
export function useSetPageTags() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ pageId, tagIds }: SetPageTagsInput) =>
      outbox.enqueue({
        kind: 'page.update',
        entityId: pageId,
        label: translate('tags.assignLabel'),
        request: { method: 'PATCH', path: `/api/pages/${pageId}`, body: { tagIds } },
      }),

    onMutate: async ({ pageId, tagIds }) => {
      await queryClient.cancelQueries({ queryKey: keys.pages.detail(pageId) });
      const previous = queryClient.getQueryData<PageDto>(keys.pages.detail(pageId));

      // The page detail carries `tags` (objects), the request carries `tagIds`. Project one
      // onto the other from the tag cache so the chips render instantly — including a tag
      // created a millisecond ago, which is in the cache optimistically but not yet on the
      // server.
      const known = queryClient.getQueryData<TagsResponse>(keys.tags.all())?.tags ?? [];
      const byId = new Map(known.map((tag) => [tag.id, tag]));

      const tags: PageTag[] = tagIds.flatMap((id) => {
        const tag = byId.get(id);
        return tag === undefined ? [] : [{ id: tag.id, name: tag.name, color: tag.color }];
      });

      queryClient.setQueryData<PageDto>(
        keys.pages.detail(pageId),
        (old) => old && { ...old, tags },
      );

      return { previous };
    },

    onError: (_error, { pageId }, context) => {
      if (context?.previous) queryClient.setQueryData(keys.pages.detail(pageId), context.previous);
    },
  });
}
